import { createSelector } from "reselect";
import { Map, List } from "immutable";
import { RootState } from "common/reducers";
import { IdType, Nullable } from "common/types";
import { QueryParamsRecord } from "common/records/QueryParams";
import { MetaPaginationRecord } from "common/records/MetaPagination";
import { DropdownRecord, fromJSON } from "common/components/Dropdown/Dropdown";

/**
 * Gets the byId Map from the state. Used in `onSortEnd` to update records.
 * @param state The RootState
 * @param props `path` - The path to the byId Map
 * @returns The byId Map
 */
export const byId = (state: RootState, props: { path: (string | number)[] }): Map<number | string, any> =>
  state.getIn(props.path) as any;

/**
 * Gets the idsManagementRecord for the given `modelName`.
 * @param state The RootState
 * @param props `modelName` - The name of the model
 * @returns The IdsManagementRecord, or `undefined` if not present in state
 */
export const idsManagementRecord = (state: RootState, props: { modelName: string }) =>
  state.getIn(["idsManagement", "byModel"])?.get(props.modelName);

/**
 * Gets the ids for the given `modelName`.
 * @param state The RootState
 * @param props `modelName` - The name of the model
 * @returns The ids
 */
export const ids: (state: RootState, props: { modelName: string }) => List<IdType> = createSelector(
  [idsManagementRecord],
  (idsManagementRecord) => idsManagementRecord?.get("ids") || List(),
);

/**
 * Check is all ids are in the store
 * @param state The RootState
 * @param props `modelName` & `idsToCheck` - The name of the model
 * @returns boolean
 */
export const idsIncluded = (state: RootState, props: { modelName: string; idsToCheck: List<number> }) => {
  const mainList = ids(state, props);
  return props.idsToCheck.every((id) => mainList.includes(id));
};

/**
 * Gets the loading flag for the given `modelName`.
 * @param state The RootState
 * @param props `modelName` - The name of the model
 * @returns loading flag
 */
export const loading: (state: RootState, props: { modelName: string }) => boolean = createSelector(
  [idsManagementRecord],
  (idsManagementRecord) => idsManagementRecord?.get("loading") || false,
);

/**
 * Gets the dirtyIds from state. Used in `onSortEnd` to update records.
 * @param state The RootState
 * @param _props
 * @returns dirtyIds
 */
export const dirtyIds = (state: RootState, _props?: any) => state.getIn(["idsManagement", "dirtyIds"]);

/**
 * Gets the queryParams from state.
 * @param state The RootState
 * @param props `modelName` - The name of the model
 * @returns queryParams
 */
export const queryParams: (state: RootState, props: { modelName: string }) => Nullable<QueryParamsRecord> =
  createSelector([idsManagementRecord], (idsManagementRecord) => idsManagementRecord?.get("queryParams"));

/**
 * Gets the `metaPagination` record for the given `modelName`.
 * @param state The RootState
 * @param props `modelName` - The name of the model
 * @returns The `metaPagination` record
 */
export const metaPagination: (state: RootState, props: { modelName: string }) => Nullable<MetaPaginationRecord> =
  createSelector([idsManagementRecord], (idsManagementRecord) => idsManagementRecord?.get("metaPagination"));

/**
 * Fetches the data records of the current page.
 *
 * @param {RootState} state The RootState
 * @param options - `modelName` - The name of the model. `path` - The path to the byId Map.
 * @returns {List<any>} List of records
 */
export const getRecordsByModel: (
  state: RootState,
  options: { modelName: string; path: (string | number)[]; includeArchived?: boolean },
) => List<any> = createSelector(
  [byId, ids, (_, props) => props],
  (modelById: Map<IdType, any>, ids: List<IdType> | undefined, props) => {
    if (!ids) {
      return List();
    }

    const interim = ids
      .map((id) => modelById.get(id))
      // Filter out archived records if `includeArchived` is false
      .filter((object: any) => (!props.includeArchived ? !object?.archived : true));

    return interim;
  },
);

/**
 * Fetches the data records of the current page, checks if it's empty, and returns a new page number
 *
 * @param state The RootState
 * @param options `modelName` - The name of the model.
 * @returns new page number if the current page is empty
 */
export const checkPageEmpty = createSelector([ids, queryParams, metaPagination], (ids, queryParams, metaPagination) => {
  if (!ids || !queryParams || !metaPagination) {
    return null;
  }

  if (ids.size > 0) {
    return null;
  }

  // Countless mode
  if (!metaPagination.total_pages) {
    if (queryParams.page > 1) {
      return metaPagination.prev_page ?? queryParams.page - 1;
    }
    return null;
  }

  // First page logic
  if (queryParams.page === 1 && metaPagination.total_pages && metaPagination.total_pages > 1) {
    return 1;
  }

  // Last page logic
  if (queryParams.page > 1 && queryParams.page === (metaPagination.total_pages || 1)) {
    return queryParams.page - 1;
  }

  // Middle page logic
  if (queryParams.page > 1 && queryParams.page < (metaPagination.total_pages || 1)) {
    return queryParams.page;
  }

  return null;
});

/**
 * Fetches the data records of the current page and returns them as dropdown options.
 *
 * @param {RootState} state The RootState
 * @param options - `modelName` - The name of the model. `path` - The path to the byId Map.
 * @returns {List<any>} List of records
 */
export const getRecordsAsDropdownOptions = createSelector(
  [getRecordsByModel],
  (records: List<any>) =>
    records.map((record) => fromJSON({ id: record.id, name: record.name })) as List<DropdownRecord>,
);
